import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { GripGlyph } from "../components/GripGlyph";
import { Icon } from "../components/Icon";
import { isUsableTarget } from "./anchorTarget";
import { clampOffsetAt, nextDragOffset, offsetCorrection, sameOffset, NO_DRAG, type DragOffset } from "./overlayDrag";
import { isLastStep } from "./tourMachine";
import type { HelpGuide } from "./types";

type Rect = { top: number; left: number; width: number; height: number };
type Pos = { top: number; left: number };

const SPOTLIGHT_PAD = 6;
const CARD_WIDTH = 328;
const GAP = 12;
/** Movement beyond this counts as a drag rather than a click — same threshold as the Help videos dock. */
const DRAG_THRESHOLD_PX = 4;
/** How often to look for a step's control while it isn't on the page yet. */
const POLL_MS = 120;

/**
 * What to tell someone to DO when a step's control isn't on screen (KEY-813): the step's own `reach`, or — when
 * the step before asked for a click that would have opened this — "press Back and click it". Null when neither
 * applies, and the hint (if any) has to carry it alone.
 */
export function reachFor(guide: HelpGuide, stepIndex: number): string | null {
  const step = guide.steps[stepIndex];
  if (step.reach) return step.reach;
  const previous = stepIndex > 0 ? guide.steps[stepIndex - 1] : null;
  if (previous?.advance === "target-click") {
    return `Press Back and click the ${previous.target ?? "highlighted control"} — this step's control appears once you have.`;
  }
  return null;
}

function readRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * Full-screen, click-through overlay for a running guide: a spotlight cut-out around the current step's
 * control plus a coach-mark card beside it. The dim is a big box-shadow on the spotlight box, so the rest
 * of the page stays interactive — the user can actually press the highlighted control. Esc stops the tour.
 *
 * The card is placed automatically beside the target, and can then be dragged out of the way by its header
 * (KEY-446): however well the placement is chosen, it will sometimes land on the very thing the step is
 * telling you to read. The drag is an offset from the computed position, so the card keeps tracking its
 * control as the page scrolls — and it is deliberately dropped on every step, because the next step's card
 * belongs beside a different control. See `overlayDrag.ts`.
 */
export function HelpOverlay({
  guide,
  stepIndex,
  onNext,
  onBack,
  onStop,
}: {
  guide: HelpGuide;
  stepIndex: number;
  onNext: () => void;
  onBack: () => void;
  onStop: () => void;
}) {
  const step = guide.steps[stepIndex];
  const total = guide.steps.length;
  const last = isLastStep(stepIndex, total);
  // KEY-813: on a step that asks for a click, the click IS the way on; Next becomes a quieter Skip.
  const clickStep = step.advance === "target-click" && !last;
  const reach = reachFor(guide, stepIndex);

  const [rect, setRect] = useState<Rect | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const [offset, setOffset] = useState<DragOffset>(NO_DRAG);
  const [dragging, setDragging] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Resolve and track the current step's target control — for the whole life of the step, not just until it is
  // first found. KEY-813: the Dynamic workspace remounts its panels whenever the selection moves, so a control
  // found once can be replaced by a fresh copy a moment later. Holding on to the old element left the spotlight
  // in the top-left corner (a detached node's rect is all zeros) and the target-click listener on a node nobody
  // could click. So one poll runs throughout: it finds the control, notices when it has gone, and finds it again.
  useEffect(() => {
    let cancelled = false;
    let el: HTMLElement | null = null;
    let ro: ResizeObserver | undefined;
    let searchingSince = Date.now();
    // After losing a control we had, give its replacement a moment before saying it's missing — a remount is
    // over in a frame or two, and flashing "First: …" at someone mid-click reads as a scolding.
    let grace = step.waitMs ?? 0;

    // A match that isn't being rendered is not a find (KEY-535). `querySelector` happily returns a control
    // hidden with `display: none`, whose rect is all zeros — which drew a 12px spotlight in the corner of the
    // page with the coach-mark beside it. Rejecting it here means the poll keeps waiting and the hint shows.
    const usable = (candidate: HTMLElement) =>
      candidate.isConnected && candidate.getClientRects().length > 0 && isUsableTarget(candidate.getBoundingClientRect());
    const find = () => {
      const found = document.querySelector<HTMLElement>(`[data-help-anchor="${step.anchor}"]`);
      return found && usable(found) ? found : null;
    };
    const update = () => {
      if (el && !cancelled && el.isConnected) {
        setRect(readRect(el));
      }
    };
    const onTargetClick = () => {
      if (step.advance === "target-click") {
        onNext();
      }
    };
    const detach = () => {
      ro?.disconnect();
      ro = undefined;
      el?.removeEventListener("click", onTargetClick);
      el = null;
    };
    const attach = (found: HTMLElement) => {
      el = found;
      setNotFound(false);
      found.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
      update();
      ro = new ResizeObserver(update);
      ro.observe(found);
      if (step.advance === "target-click") {
        found.addEventListener("click", onTargetClick, { once: true });
      }
    };
    // `waitMs` is a GRACE PERIOD, not a deadline (KEY-535): the poll runs for the life of the step, and `waitMs`
    // only decides how long before the "First: …" line and the hint appear alongside it.
    const tick = () => {
      if (cancelled) return;
      if (el) {
        if (usable(el)) return;
        detach();
        setRect(null);
        searchingSince = Date.now();
        grace = Math.max(step.waitMs ?? 0, 600);
      }
      const found = find();
      if (found) {
        attach(found);
      } else if (Date.now() - searchingSince > grace) {
        setNotFound(true);
      }
    };

    const immediate = find();
    if (immediate) {
      attach(immediate);
    } else {
      setRect(null);
      setNotFound(false);
    }
    const poll = setInterval(tick, POLL_MS);

    const onMove = () => update();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      cancelled = true;
      clearInterval(poll);
      detach();
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [step.anchor, step.waitMs, step.advance, stepIndex, onNext]);

  // A drag belongs to the step it was given to: the next step's card is placed against a different
  // control, so it starts from the automatic position rather than carrying a stale offset there.
  useEffect(() => {
    setOffset(NO_DRAG);
  }, [stepIndex, step.anchor]);

  const dragOrigin = useRef<{ pointerX: number; pointerY: number; top: number; left: number; offset: DragOffset } | null>(null);
  const movedRef = useRef(false);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return; // left button / touch only
      // Never start a drag from a control inside the handle. Capturing the pointer retargets the
      // pointerup to the handle, so the control's own click would never fire — which would break Stop,
      // since it sits in the header row that IS the handle. (The same trap `useDraggableDock` hit.)
      const control = (event.target as HTMLElement | null)?.closest('button, a, input, textarea, select, [role="button"]');
      if (control && control !== event.currentTarget) return;
      const rect = cardRef.current?.getBoundingClientRect();
      if (!rect) return;
      // The card's real position, whether it was placed beside a target or centred with a transform.
      dragOrigin.current = { pointerX: event.clientX, pointerY: event.clientY, top: rect.top, left: rect.left, offset };
      movedRef.current = false;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [offset],
  );

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    const dx = event.clientX - origin.pointerX;
    const dy = event.clientY - origin.pointerY;
    if (!movedRef.current && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
    if (!movedRef.current) {
      movedRef.current = true;
      setDragging(true);
    }
    const card = cardRef.current?.getBoundingClientRect();
    if (!card) return;
    setOffset(nextDragOffset(origin, { dx, dy }, card, { width: window.innerWidth, height: window.innerHeight }));
  }, []);

  const onPointerUp = useCallback((event: React.PointerEvent) => {
    if (!dragOrigin.current) return;
    dragOrigin.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    movedRef.current = false;
    setDragging(false);
  }, []);

  // Shrinking the window can strand a card that was dragged near an edge. The placement effect below
  // re-clamps whenever the spotlighted control moves, but that only fires while there IS a control:
  // when the step's target can't be found the card is centred instead, and nothing would pull it back.
  // Clamping the rendered rect covers both, since the correction is just folded into the offset.
  useEffect(() => {
    const onResize = () => {
      const el = cardRef.current;
      if (!el) return;
      const correction = offsetCorrection(el.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight });
      if (sameOffset(correction, NO_DRAG)) return;
      setOffset((current) => ({ dx: current.dx + correction.dx, dy: current.dy + correction.dy }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Esc stops the tour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onStop();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onStop]);

  // Place the coach-mark next to the target (below, flipping above when there's no room). When there's no
  // target (control not found), it's centred — see cardStyle.
  useLayoutEffect(() => {
    if (!rect || !cardRef.current) {
      setPos(null);
      return;
    }
    const card = cardRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = rect.top + rect.height + GAP;
    if (top + card.height > vh - 8 && rect.top - GAP - card.height > 8) {
      top = rect.top - GAP - card.height;
    }
    top = Math.max(8, Math.min(top, vh - card.height - 8));
    const left = Math.max(8, Math.min(rect.left, vw - card.width - 8));
    setPos({ top, left });
    // A card dragged near an edge would otherwise be stranded when the window shrinks or the target
    // moves, so the offset is re-clamped against the new placement rather than thrown away.
    setOffset((current) => {
      const clamped = clampOffsetAt({ top, left }, current, card, { width: vw, height: vh });
      return sameOffset(clamped, current) ? current : clamped;
    });
  }, [rect]);

  const cardStyle: CSSProperties = pos
    ? { position: "fixed", top: pos.top + offset.dy, left: pos.left + offset.dx }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: `translate(calc(-50% + ${offset.dx}px), calc(-50% + ${offset.dy}px))`,
      };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, pointerEvents: "none" }}>
      {rect && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: rect.top - SPOTLIGHT_PAD,
            left: rect.left - SPOTLIGHT_PAD,
            width: rect.width + SPOTLIGHT_PAD * 2,
            height: rect.height + SPOTLIGHT_PAD * 2,
            borderRadius: 10,
            border: "2px solid var(--accent)",
            boxShadow: "0 0 0 9999px rgba(15,14,34,.55)",
            pointerEvents: "none",
            transition: "top .15s ease, left .15s ease, width .15s ease, height .15s ease",
          }}
        />
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-label={`${guide.title} — help`}
        style={{
          ...cardStyle,
          width: CARD_WIDTH,
          maxWidth: "calc(100vw - 16px)",
          pointerEvents: "auto",
          background: "var(--surface)",
          border: "var(--border-w) solid var(--border)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          padding: 16,
          animation: "kd-pop .18s ease",
        }}
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          title="Drag to move this help card"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
            touchAction: "none",
            userSelect: "none",
            cursor: dragging ? "grabbing" : "grab",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <GripGlyph />
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                flex: "none",
                background: "var(--accent-soft)",
                color: "var(--accent-text)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="help" size={13} />
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {guide.title}
            </span>
          </div>
          <button
            onClick={onStop}
            aria-label="Stop help"
            title="Stop"
            style={{
              width: 26,
              height: 26,
              flex: "none",
              borderRadius: 99,
              border: "none",
              background: "var(--surface-inset)",
              color: "var(--text-muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="x" size={14} />
          </button>
        </div>

        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text)", marginTop: 10 }}>{step.title}</div>
        <p style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.5, margin: "6px 0 0" }}>{step.body}</p>
        {clickStep && rect && (
          <p style={{ fontSize: 13, fontWeight: 600, color: "var(--accent-text)", lineHeight: 1.45, margin: "8px 0 0" }}>
            Click the highlighted {step.target ?? "control"} to continue.
          </p>
        )}
        {notFound && reach && (
          <p
            role="status"
            style={{
              fontSize: 13,
              color: "var(--text)",
              lineHeight: 1.45,
              margin: "10px 0 0",
              padding: "8px 10px",
              background: "var(--accent-soft)",
              border: "var(--border-w) solid var(--accent-border)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            <strong>First:</strong> {reach}
          </p>
        )}
        {notFound && step.hint && (
          <p
            style={{
              fontSize: 12.5,
              color: "var(--text-faint)",
              lineHeight: 1.45,
              margin: "10px 0 0",
              padding: "8px 10px",
              background: "var(--surface-inset)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            {step.hint}
          </p>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text-faint)", fontWeight: 600 }}>
            Step {stepIndex + 1} of {total}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onBack}
              disabled={stepIndex === 0}
              style={{
                font: "inherit",
                fontSize: 13,
                fontWeight: 600,
                padding: "6px 12px",
                borderRadius: "var(--radius-sm)",
                border: "var(--border-w) solid var(--border)",
                background: "var(--surface)",
                color: "var(--text-muted)",
                cursor: stepIndex === 0 ? "default" : "pointer",
                opacity: stepIndex === 0 ? 0.5 : 1,
              }}
            >
              Back
            </button>
            <button
              onClick={onNext}
              title={clickStep ? "Move on without clicking it — the next step will say what to do first" : undefined}
              style={
                clickStep
                  ? {
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "6px 12px",
                      borderRadius: "var(--radius-sm)",
                      border: "var(--border-w) solid var(--border)",
                      background: "var(--surface)",
                      color: "var(--text-muted)",
                      cursor: "pointer",
                    }
                  : {
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 700,
                      padding: "6px 14px",
                      borderRadius: "var(--radius-sm)",
                      border: "none",
                      background: "var(--accent)",
                      color: "var(--on-accent, #fff)",
                      cursor: "pointer",
                    }
              }
            >
              {last ? "Done" : clickStep ? "Skip" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
